import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import './ValidateOtpPage.scss';
import BtnSubmit from '../../../../Shared/Buttons/BtnSubmit';
import TextInput from '../../../../Shared/TextInput/TextInput';
import Config from '../../../../../Config';
import { reportDWH, setLoginDetails, goToPageWithoutHistory } from '../../EsimUpgradeBlockSlice';
import {
    createOtpAsync,
    validateOtpAsync,
    proceedAfterFirstOtpValidatedAsync,
    proceedAfterSecurityOtpValidatedAsync,
    getOtpValidationErrorMessage,
    getSecurityOtpTargetPhone,
    isRestrictedCodeRequired,
    OtpStepType,
    isContactPerson,
    OtpValidateError,
} from '../../EsimUpgradeBlockAsyncThunk';
import { gtag4 } from '../../../../../utilities/analytics/tracking';
import codeNotReceivedIcn from '../Svg/codeNotReceivedIcn.svg';
import voiceOtpIcn from '../Svg/codeNotReceivedVoiseIcn.svg';

const ValidateOtpPage = (props) => {
    const otpStepType = props.otpStepType || OtpStepType.LOGIN;
    const [code, setCode] = useState('');
    const [restrictCode, setRestrictCode] = useState('');
    const [otpError, setOtpError] = useState('');
    const [restrictCodeError, setRestrictCodeError] = useState('');
    const securityOtpSentRef = useRef(false);
    const isContact = isContactPerson(props.userDetails);

    useEffect(() => {
        setCode('');
        setRestrictCode('');
        setOtpError('');
        setRestrictCodeError('');
        securityOtpSentRef.current = false;
    }, [otpStepType]);

    useEffect(() => {
        if (otpStepType === OtpStepType.LOGIN && !props.loginDetails?.otpTicketId) {
            props.goToPageWithoutHistory('otpPage');
        }
    }, [otpStepType, props.loginDetails?.otpTicketId]);

    const isSecurityExtendedOtp = otpStepType === OtpStepType.SECURITY && !isContact;

    const getContactPhone = () =>
        props.userDetails?.contactNumber ||
        props.userDetails?.LoginData?.ContactValue ||
        '';

    const getSecurityOtpSendPhone = () =>
        isSecurityExtendedOtp
            ? (
                props.loginDetails?.securityOtpPhone ||
                getSecurityOtpTargetPhone(props.authentication, props.loginDetails) ||
                getContactPhone()
            )
            : getContactPhone();

    const targetPhone = (() => {
        if (otpStepType === OtpStepType.SECURITY) {
            if (isSecurityExtendedOtp) {
                return props.loginDetails?.securityOtpPhone || '';
            }
            return getContactPhone();
        }
        return props.targetPhone || props.loginDetails?.subscriberNo;
    })();

    const getMaskedPhone = (phone) => {
        if (!phone || phone.length < 10) return phone || '';
        return `${phone.slice(0, 3)}**${phone.slice(5)}`;
    };

    useEffect(() => {
        if (otpStepType === OtpStepType.SECURITY && !securityOtpSentRef.current) {
            const phoneForSend = getSecurityOtpSendPhone();
            if (!phoneForSend) return;

            securityOtpSentRef.current = true;
            props.createOtpAsync({
                subscriberNo: phoneForSend,
                isExtendedOtp: isSecurityExtendedOtp,
            }).then(res => updateSecurityOtpDetails(res?.payload));
        }
        gtag4({ event: 'EsimUpgrade_Step1_OtpFieldPage', step: '1', action: 'OtpFieldPage' });
        props.reportDWH({ actionType: 'eSIM Verification Page', status: 'Ongoing', messageText: 'eSIM Verification Page Displayed' });
    }, [otpStepType]);

    useEffect(() => {
        if ('OTPCredential' in window) {
            async function fetchOtp() {
                try {
                    const otp = await navigator.credentials.get({
                        otp: { transport: ['sms'] },
                    });
                    if (otp?.code) {
                        setCode(otp.code);
                    }
                } catch (err) {
                    console.error('Error fetching OTP:', err);
                }
            }
            fetchOtp();
        }
    }, []);

    const getTitle = () => {
        if (otpStepType !== OtpStepType.SECURITY) return '';
        if (isContact) return <span className='otp-contact-title'>לשמירה על החשבון והמידע שלך</span>;
        return (
            <span className='otp-security-title'>
                <span className='otp-security-title-line'>כדי להמשיך נדרש אישור</span>
                <span className='otp-security-title-line'>
                    מבעל החשבון{' '}
                    {targetPhone && (
                        <span className='otp-security-title-phone masked-phone' dir='ltr'>{getMaskedPhone(targetPhone)}</span>
                    )}
                </span>
            </span>
        );
    };

    const getInstruction = () => {
        if (otpStepType !== OtpStepType.SECURITY) return '';
        return isContact ? 'איזה קוד קיבלת?' : 'מה הקוד שהתקבל?';
    };

    const renderSubTitle = () => {
        if (otpStepType === OtpStepType.LOGIN) {
            return (<>
                שלחנו קוד ל-<span className='masked-phone' dir='ltr'>{getMaskedPhone(targetPhone)}</span>
            </>);
        }
        if (otpStepType === OtpStepType.SECURITY && isContact) {
            return 'שלחנו לך קוד אימות נוסף';
        }
        return '';
    };

    const usesLoginHeader = otpStepType === OtpStepType.LOGIN;

    return (
        <div className='esim-upgrade-validate-otp-page-wrapper'>
            <div className='esim-upgrade-validate-otp-page'>
                {usesLoginHeader ? (
                    <div className='otp-first-header'>
                        <div className='main-icon-otp'>
                            <img src={Config.epiBaseUrl + props.otpRecievedCodeIcon} alt='' />
                        </div>
                        <div className='sub-title'>{renderSubTitle()}</div>
                    </div>
                ) : (
                    <div className='otp-header'>
                        <div className='otp-header-title'>{getTitle()}</div>
                        {renderSubTitle() && (
                            <div className='otp-header-subtitle'>{renderSubTitle()}</div>
                        )}
                        {getInstruction() && (
                            <div className='otp-header-instruction'>{getInstruction()}</div>
                        )}
                    </div>
                )}
                <div className='otp-container'>
                    <TextInput
                        className='otp-input-field'
                        name='validate-otp-code'
                        type='text'
                        value={code}
                        onChange={e => handleChange(e.target.value)}
                        maxLength={6}
                        autoFocus
                        autoComplete='one-time-code'
                        inputMode='numeric'
                        pattern='[0-9]*'
                        placeholder='קוד אימות'
                    />
                    {otpError && <div className='error-message'>{otpError}</div>}
                </div>
                <div className='resend-buttons'>
                    <a href='#' onClick={(e) => handleResendOtp(e)}>
                        <span className='otp-link-icon'><img src={codeNotReceivedIcn} alt='' /></span>
                        <span className='otp-link-text'>לא קיבלתי קוד,<br />תשלחו שוב</span>
                    </a>
                    <div className='buttons-seperator'></div>
                    <a href='#' onClick={(e) => handleSendVoiceOtp(e)}>
                        <span className='otp-link-icon'><img src={voiceOtpIcn} alt='' /></span>
                        <span className='otp-link-text'>שלחו לי <br />בהודעה קולית</span>
                    </a>
                </div>
                {props.loginDetails?.restrictedCodeRequired ? (
                    <div className='restrict-code-container'>
                        <p className='restrict-code-label'>קוד זיהוי של איש הקשר</p>
                        <TextInput
                            className='restrict-code-field'
                            name='restrict-code'
                            type='text'
                            value={restrictCode}
                            onChange={(e) => {
                                setRestrictCode(e.target.value);
                                setRestrictCodeError('');
                            }}
                            autoComplete='off'
                            required='required'
                            placeholder='קוד זיהוי'
                            error={restrictCodeError}
                        />
                    </div>
                ) : null}
                <div className='button-wrapper'>
                    <BtnSubmit
                        isBusiness={false}
                        text='בואו נתקדם'
                        onclick={handlePageContinue}
                        formid='esim-block'
                        isDisplayArrows={false}
                        isDisplayWhatsAppIcon={false}
                    />
                </div>
            </div>
        </div>
    );

    function handleChange(otp) {
        const val = (otp || '').slice(0, 6);
        setCode(val);
        if (/\D/.test(val)) {
            setOtpError('אנא הקלד/י רק ספרות');
        } else {
            setOtpError('');
        }
    }

    function updateSecurityOtpDetails(payload) {
        if (!payload?.Body?.isSuccess) return;
        props.setLoginDetails({
            securityOtpTicketId: payload.Body.message,
            securityOtpPhone: payload.Body.extra?.contactNumber || props.loginDetails.securityOtpPhone,
            restrictedCodeRequired: isRestrictedCodeRequired(payload.Body),
        });
    }

    function updateLoginOtpDetails(payload) {
        if (!payload?.Body?.isSuccess) return;
        props.setLoginDetails({
            otpTicketId: payload.Body.message,
            restrictedCodeRequired: isRestrictedCodeRequired(payload.Body),
        });
    }

    function getOtpSendParams() {
        if (otpStepType === OtpStepType.LOGIN) {
            return { subscriberNo: props.loginDetails.subscriberNo };
        }
        return {
            subscriberNo: getSecurityOtpSendPhone(),
            isExtendedOtp: isSecurityExtendedOtp,
        };
    }

    async function handleResendOtp(e) {
        e.preventDefault();
        setOtpError('');
        setRestrictCodeError('');
        const res = await props.createOtpAsync(getOtpSendParams());
        if (otpStepType === OtpStepType.SECURITY) {
            updateSecurityOtpDetails(res?.payload);
        } else if (otpStepType === OtpStepType.LOGIN) {
            updateLoginOtpDetails(res?.payload);
        }
    }

    async function handleSendVoiceOtp(e) {
        e.preventDefault();
        setOtpError('');
        setRestrictCodeError('');
        const res = await props.createOtpAsync(getOtpSendParams());
        if (otpStepType === OtpStepType.SECURITY) {
            updateSecurityOtpDetails(res?.payload);
        } else if (otpStepType === OtpStepType.LOGIN) {
            updateLoginOtpDetails(res?.payload);
        }
    }

    function applyValidationError(res) {
        const errorType = res?.payload?.errorType;
        if (errorType === OtpValidateError.WRONG_RESTRICT_CODE) {
            setRestrictCodeError(getOtpValidationErrorMessage(res));
            return;
        }
        setOtpError(getOtpValidationErrorMessage(res));
    }

    async function handlePageContinue() {
        setOtpError('');
        setRestrictCodeError('');
        if (/\D/.test(code)) {
            setOtpError('אנא הקלד/י רק ספרות');
            return;
        }
        if (!/^\d{6}$/.test(code)) {
            setOtpError('יש להזין 6 ספרות');
            return;
        }
        const restrictValue = restrictCode.trim();
        if (props.loginDetails?.restrictedCodeRequired && !restrictValue) {
            setRestrictCodeError('יש להזין קוד זיהוי');
            return;
        }
        props.reportDWH({ actionType: 'eSIM Verification Page', status: 'Ongoing', messageText: 'eSIM Verification Button Clicked&Approved' });
        gtag4({ event: 'EsimUpgrade_Step1_OtpFieldButton', step: '1', action: 'OtpFieldButton' });

        switch (otpStepType) {
            case OtpStepType.LOGIN: {
                const res = await props.validateOtpAsync({
                    subscriberNo: props.loginDetails.subscriberNo,
                    otpTicketId: props.loginDetails.otpTicketId,
                    code: code,
                    restrictCode: restrictValue,
                });
                if (!res?.payload?.success) {
                    applyValidationError(res);
                    return;
                }
                await props.proceedAfterFirstOtpValidatedAsync();
                break;
            }
            case OtpStepType.SECURITY: {
                const res = await props.validateOtpAsync({
                    subscriberNo:
                        getSecurityOtpTargetPhone(props.authentication, props.loginDetails) ||
                        getContactPhone(),
                    otpTicketId: props.loginDetails.securityOtpTicketId,
                    code: code,
                    isExtendedOtp: isSecurityExtendedOtp,
                    securityOtpPhone: props.loginDetails.securityOtpPhone,
                    restrictCode: restrictValue,
                });
                if (!res?.payload?.success) {
                    applyValidationError(res);
                    return;
                }
                gtag4({ event: 'EsimUpgrade_SecurityOtp_Success', step: '3', action: 'SecurityOtpSuccess' });
                await props.proceedAfterSecurityOtpValidatedAsync();
                break;
            }
            default:
                break;
        }
    }
};

const mapStateToProps = (state, ownProps) => {
    return {
        loginDetails: state.esimUpgradeBlock.loginDetails,
        authentication: state.authentication,
        userDetails: state.authentication.loginData?.userDetails,
        otpTopIcon: state.esimUpgradeBlock.esimBlockData.otpTopIcon,
        otpRecievedCodeIcon: state.esimUpgradeBlock.esimBlockData.otpRecievedCodeIcon,
        otpStepType: ownProps.otpStepType,
        targetPhone: ownProps.targetPhone,
    };
};

const mapDispatchToProps = {
    reportDWH,
    createOtpAsync,
    validateOtpAsync,
    proceedAfterFirstOtpValidatedAsync,
    proceedAfterSecurityOtpValidatedAsync,
    setLoginDetails,
    goToPageWithoutHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(ValidateOtpPage);
